/**
 * @typedef {{ id: number, code: string, displayName: string }} CompanySource
 * @typedef {{ id: string, companySourceId: number | null, numCases: number, expiration: string, supplierLot: string }} PalletLine
 * @typedef {{ id: string, lines: PalletLine[] }} Pallet
 * @typedef {{ sourceWarnings: Map<number, string>, globalMessages: string[] }} AllocationVarianceWarnings
 *
 * @param {Pallet[]} pallets
 * @param {CompanySource[]} companySourceOptions
 * @returns {{ grandTotal: number, bySource: Array<{ companySource: CompanySource, numCases: number }> }}
 */
function summarizeEnteredCasesBySource(pallets, companySourceOptions) {
    const knownSourceIds = new Set(companySourceOptions.map((source) => Number(source.id)));
    const totalsBySourceId = new Map();
    let grandTotal = 0;

    for (const pallet of pallets) {
        for (const line of pallet.lines) {
            const numCases = Number(line.numCases) || 0;
            grandTotal += numCases;

            if (line.companySourceId != null) {
                const sourceId = Number(line.companySourceId);
                if (knownSourceIds.has(sourceId)) {
                    totalsBySourceId.set(sourceId, (totalsBySourceId.get(sourceId) || 0) + numCases);
                }
            }
        }
    }

    return {
        grandTotal,
        bySource: companySourceOptions.map((source) => ({
            companySource: source,
            numCases: totalsBySourceId.get(Number(source.id)) ?? 0,
        })),
    };
}

/** @returns {AllocationVarianceWarnings} */
function emptyVarianceWarnings() {
    return { sourceWarnings: new Map(), globalMessages: [] };
}

/**
 * @param {Array<{ companySource: CompanySource, numCases: number }>} suggestedAllocations
 * @param {{ grandTotal: number, bySource: Array<{ companySource: CompanySource, numCases: number }> }} enteredSummary
 * @param {{ isThirdPartyAsn?: boolean }} [options]
 * @returns {AllocationVarianceWarnings}
 */
function getAllocationVarianceWarnings(suggestedAllocations, enteredSummary, { isThirdPartyAsn = false } = {}) {
    if (!suggestedAllocations?.length) {
        return emptyVarianceWarnings();
    }

    const expectedTotal = suggestedAllocations.reduce((sum, allocation) => sum + allocation.numCases, 0);
    const enteredTotal = enteredSummary.grandTotal ?? 0;

    if (isThirdPartyAsn) {
        if (enteredTotal !== expectedTotal) {
            return {
                sourceWarnings: new Map(),
                globalMessages: [
                    `Entered cases (${enteredTotal}) do not match expected allocation (${expectedTotal}).`,
                ],
            };
        }

        return emptyVarianceWarnings();
    }

    /** @type {Map<number, string>} */
    const sourceWarnings = new Map();
    const enteredBySourceId = new Map(
        enteredSummary.bySource.map((entry) => [Number(entry.companySource.id), entry.numCases]),
    );

    for (const allocation of suggestedAllocations) {
        const sourceId = Number(allocation.companySource.id);
        const expectedCases = allocation.numCases;
        const enteredCases = enteredBySourceId.get(sourceId) ?? 0;

        if (enteredCases !== expectedCases) {
            sourceWarnings.set(
                sourceId,
                `${allocation.companySource.displayName}: entered ${enteredCases} cases, expected ${expectedCases}.`,
            );
        }
    }

    const assignedTotal = enteredSummary.bySource.reduce((sum, entry) => sum + entry.numCases, 0);
    const globalMessages =
        enteredTotal > assignedTotal ? ['Some entered cases are not assigned to a company source.'] : [];

    return { sourceWarnings, globalMessages };
}

export { getAllocationVarianceWarnings, summarizeEnteredCasesBySource };
